/* eslint-disable @typescript-eslint/no-explicit-any */
import { Spacer, Text } from "@hackthenorth/north";
import { GraphQLError } from "graphql";
import { Button, Link, Spinner, TextInput } from "north.js";
import React, { useEffect, useState } from "react";
import { Link as RouterLink, Prompt } from "react-router-dom";
import styled from "styled-components";

import "twin.macro";

import {
  PageWrapper,
  DateTextInput,
  Dropzone,
  IMAGE_FILE_TYPES,
  FileLink,
  MarkdownDocument,
} from "src/shared/components";
import { successToast, errorToast } from "src/shared/components/Toast";
import { Route } from "src/shared/constants/route";
import { useHackerContext, useUserContext } from "src/shared/contexts";
import {
  Field,
  HackerStage,
  PageStage,
} from "src/shared/contexts/HackerContext/types";
import { useHackerState } from "src/shared/contexts/HackerContext/useHackerState";
import { useDeviceSize, useIsMounted, useRsvpFlow } from "src/shared/hooks";
import { Nullable } from "src/shared/utils/typescript";
import { formatValidationIssues } from "src/shared/utils/validationMessages";
import { ArrowRight, WaiverDownload } from "src/static/icons";

import { useRequestTicketMutation } from "../../../register/graphql/requestTicket.generated";
import { useFileDropzone } from "../career/useFileDropzone";
import RsvpModal from "../home/modals/rsvpModal";
import RsvpSidebar from "../rsvp-sidebar";

import {
  ACCEPTED_FIELDS,
  TERMS_AND_CONDITIONS_MD,
  VALIDATORS,
} from "./constants";
import { doLegalNamesMatch } from "./signature";
import {
  formatBirthDate,
  isAdultOnDate,
  WAIVER_HASH,
  WAIVER_VERSION,
} from "./waiver";

const isEmpty = (s: Nullable<string> | undefined) =>
  s == null || s.trim() === "";

const ConfirmationPage = () => {
  const isTabletOrSmaller = useDeviceSize("tablet");
  const isMounted = useIsMounted();
  const { isHacker, isVolunteer } = useRsvpFlow();
  const { info } = useUserContext();
  const {
    updateResponses,
    isLoading,
    navigateNext,
    updateStage,
    navigateBack,
    hasUnsavedAutosave,
  } = useHackerContext();
  const [requestTicket] = useRequestTicketMutation({
    context: { unauthenticated: true },
  });
  const { responsesState, setResponsesState, isValid } = useHackerState(
    ACCEPTED_FIELDS,
    VALIDATORS
  );

  const [showErrors, setShowErrors] = useState(false);
  const [showRsvpModal, setShowRsvpModal] = useState(false);
  const [isBlocking, setIsBlocking] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const birthDate = responsesState[Field.SHORT_BIRTHDATE] ?? "";
  const legalName = responsesState[Field.LEGAL_NAME]?.trim();
  const formattedBirthDate = formatBirthDate(birthDate);
  const isAdult = isAdultOnDate(birthDate);
  const isAgeConsentValid = isAdult !== null;
  const isGuardianNameValid =
    isAdult !== false || !isEmpty(responsesState[Field.GUARDIAN_NAME]);
  const isGuardianContactValid =
    isAdult !== false || !isEmpty(responsesState[Field.GUARDIAN_CONTACT]);
  const isWaiverValid =
    isAdult !== false || responsesState[Field.WAIVER_CONSENT] !== null;
  const isAdultSignatureValid =
    isAdult !== true ||
    doLegalNamesMatch(
      responsesState[Field.WAIVER_SIGNATURE],
      responsesState[Field.LEGAL_NAME]
    );
  const previousStepsComplete = isHacker
    ? responsesState[Field.PROFILE_RSVP_STAGE] === PageStage.COMPLETED
    : responsesState[
        isVolunteer ? Field.PERSONAL_RSVP_STAGE : Field.BADGE_RSVP_STAGE
      ] === PageStage.COMPLETED;

  useEffect(() => {
    if (isAdult !== null && responsesState[Field.AGE_18] !== isAdult) {
      setResponsesState(Field.AGE_18, isAdult);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdult, responsesState[Field.AGE_18]]);

  const ACCEPTED_FILE_TYPES = IMAGE_FILE_TYPES;

  const {
    dropzoneInputRef,
    fetchedFile,
    loading: isFileLoading,
    error: fileError,
    addFile,
    deleteFile,
    onFileError,
    setDropzoneErrorMessage,
  } = useFileDropzone(
    responsesState[Field.WAIVER_CONSENT]?.[0],
    (files) => {
      setResponsesState(Field.WAIVER_CONSENT, files);
    },
    ACCEPTED_FILE_TYPES
  );

  useEffect(() => {
    if (
      isValid &&
      isAgeConsentValid &&
      isGuardianNameValid &&
      isGuardianContactValid &&
      isWaiverValid &&
      isAdultSignatureValid &&
      isMounted
    ) {
      navigateNext();
    }
    if (!previousStepsComplete) {
      navigateBack();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitting]);

  useEffect(() => {
    if (isValid && isMounted) {
      navigateNext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitting]);

  const onSave = async () => {
    if (
      isValid &&
      previousStepsComplete &&
      isAgeConsentValid &&
      isGuardianNameValid &&
      isGuardianContactValid &&
      isWaiverValid &&
      isAdultSignatureValid
    ) {
      try {
        const responses = {
          ...responsesState,
          [Field.AGE_18]: isAdult,
          [Field.WAIVER_HASH]: WAIVER_HASH,
          [Field.WAIVER_VERSION]: WAIVER_VERSION,
          [Field.WAIVER_SIGNED_AT]: new Date().toISOString(),
          [Field.CONFIRMATION_RSVP_STAGE]: PageStage.COMPLETED,
        };

        if (fetchedFile && !isFileLoading && !fileError) {
          responses[Field.WAIVER_CONSENT] = fetchedFile;
        }

        const { errors } = await updateResponses(responses);

        if (errors) {
          throw new Error(
            "We weren't able to submit your answers, please try again."
          );
        }

        setShowRsvpModal(true);
      } catch (e) {
        if (typeof e === "object") {
          errorToast((e as GraphQLError)?.message);
        } else {
          errorToast(e.message);
        }
      }
    } else {
      window.scrollTo(0, 0);
      errorToast(
        formatValidationIssues([
          { label: "Date of birth", isValid: isAgeConsentValid },
          { label: "Parent or guardian name", isValid: isGuardianNameValid },
          {
            label: "Parent or guardian contact information",
            isValid: isGuardianContactValid,
          },
          {
            label: isAdult === false ? "Signed waiver" : "Waiver signature",
            isValid: isAdult === false ? isWaiverValid : isAdultSignatureValid,
          },
          {
            label: "Previous RSVP sections",
            isValid: previousStepsComplete,
          },
        ])
      );
      setShowErrors(true);
    }
  };

  const onConfirm = async () => {
    try {
      const { errors } = await updateStage(HackerStage.CONFIRMED);

      if (errors) {
        throw new Error(
          "We weren't able to submit your answers, please try again."
        );
      }

      try {
        await requestTicket({ variables: { email: info?.email ?? "" } });
      } catch (ticketError) {
        console.warn("Ticket request failed after RSVP", ticketError);
      }

      successToast("Answers successfully submitted!");
      setSubmitting(true);
      setShowRsvpModal(false);
    } catch (e) {
      if (typeof e === "object") {
        errorToast((e as GraphQLError)?.message);
      } else {
        errorToast(e.message);
      }
    }
  };

  return (
    <PageWrapper>
      <Prompt
        when={isBlocking && hasUnsavedAutosave && !submitting}
        message="You have unsaved changes, are you sure you want to move away from this page?"
      />
      {isTabletOrSmaller && <RsvpSidebar currentRoute="Confirmation" />}

      <Heading1Text>Confirmation</Heading1Text>
      <Text mods="grey">
        All questions are required unless stated otherwise.
      </Text>
      <RsvpModal
        isOpen={showRsvpModal}
        onConfirm={onConfirm}
        onCancel={() => setShowRsvpModal(false)}
      />

      <Spacer height={42} />
      <Text mods="bold textBody">Date of birth</Text>
      <Spacer height={12} />
      <DisabledDateTextInput
        value={formattedBirthDate}
        disabled
        aria-readonly="true"
      />
      <Spacer height={8} />
      <Text mods="caption grey">
        This date of birth is from Personal Information. If it is incorrect,
        please{" "}
        <PersonalInformationLink to={Route.HACKER_PERSONAL}>
          update it
        </PersonalInformationLink>{" "}
        before submitting your RSVP.
      </Text>

      <Spacer height={24} />
      <Text mods="bold textBody">
        {isAdult === true
          ? "Based on your date of birth, you are currently 18 years of age or older."
          : isAdult === false
          ? "Based on your date of birth, you are currently under 18 years of age."
          : "We could not determine your current age."}
      </Text>
      {showErrors && !isAgeConsentValid && (
        <ValidationMessage>
          We do not have a valid date of birth on file. Please contact the Hack
          the North team for help.
        </ValidationMessage>
      )}

      {isAdult === true && (
        <>
          <Spacer height={42} />
          <Text mods="bold textBody">
            Terms and Conditions, Code of Conduct, and Release
          </Text>
          <Spacer height={12} />
          <WaiverPreview tabIndex={0} aria-label="Full event waiver">
            <MarkdownDocument content={TERMS_AND_CONDITIONS_MD} />
          </WaiverPreview>
          <Spacer height={12} />
          <Text mods="grey">Waiver version {WAIVER_VERSION}</Text>
        </>
      )}

      {isAdult === false && (
        <div>
          <Spacer height={24} />
          <Text mods="grey">
            Participants under 18 must have a parent or guardian read and sign
            Hack the North’s Terms and Conditions and Code of Conduct.
          </Text>

          <Spacer height={24} />
          <Text mods="bold textBody">Parent/Guardian’s full name</Text>
          <Container isValid={isGuardianNameValid} showErrors={showErrors}>
            <InputWrapper
              type="text"
              placeholder="Enter your parent or guardian’s full name"
              value={responsesState[Field.GUARDIAN_NAME] ?? ""}
              onChange={(e: any) => {
                setIsBlocking(true);
                setResponsesState(Field.GUARDIAN_NAME, e.currentTarget.value);
              }}
            />
          </Container>

          <Spacer height={24} />
          <Text mods="bold textBody">
            Parent/Guardian’s email or phone number
          </Text>
          <Container isValid={isGuardianContactValid} showErrors={showErrors}>
            <InputWrapper
              type="text"
              placeholder="Enter an email or phone number"
              value={responsesState[Field.GUARDIAN_CONTACT] ?? ""}
              onChange={(e: any) => {
                setIsBlocking(true);
                setResponsesState(
                  Field.GUARDIAN_CONTACT,
                  e.currentTarget.value
                );
              }}
            />
          </Container>
          <Spacer height={24} />
          <Text mods="bold textBody">
            Terms and Conditions and Code of Conduct
          </Text>
          <div>
            <DownloadWaiver
              download
              href={`${window.location.origin}/waiver.pdf`}
            >
              <Button size="md">
                <FlexRow>
                  <WaiverDownload />
                  <Spacer width={10} />
                  Download waiver
                </FlexRow>
              </Button>
            </DownloadWaiver>
          </div>

          <Spacer height={12} />
          <p>
            Please download the above waiver and upload a copy signed by a
            parent or guardian.
          </p>
          <Spacer height={12} />
          <Dropzone
            max={1}
            label={
              <span>
                <b>Upload a file</b> or drag and drop
              </span>
            }
            acceptedFileTypes={ACCEPTED_FILE_TYPES}
            onFilesUpload={addFile}
            onError={onFileError}
            ref={dropzoneInputRef}
            onBlur={() => setDropzoneErrorMessage("")}
          />
          {fetchedFile && (
            <>
              <Spacer height={8} />
              <FileLink file={fetchedFile} deleteFile={deleteFile} />
            </>
          )}
        </div>
      )}

      {isAdult === true && (
        <div>
          <Spacer height={24} />
          <Text mods="bold textBody">Full legal name</Text>
          <Spacer height={8} />
          <Text mods="grey">
            Enter your full legal name exactly as provided in Personal
            Information to sign the waiver{legalName ? ` (${legalName})` : ""}.
          </Text>
          <Spacer height={12} />
          <Container isValid={isAdultSignatureValid} showErrors={showErrors}>
            <InputWrapper
              type="text"
              placeholder="Enter your full legal name"
              value={responsesState[Field.WAIVER_SIGNATURE] ?? ""}
              onChange={(e: any) => {
                setIsBlocking(true);
                setResponsesState(
                  Field.WAIVER_SIGNATURE,
                  e.currentTarget.value
                );
              }}
            />
          </Container>
          {showErrors && !isAdultSignatureValid && (
            <ValidationMessage>
              Your signature must match the full legal name you provided.
            </ValidationMessage>
          )}
        </div>
      )}

      <Spacer height={100} />
      <FlexRow>
        <Button color="primary" disabled={isLoading} onClick={onSave} size="md">
          {isLoading ? (
            <Spinner />
          ) : (
            <FlexRow>
              <p>Submit RSVP</p>
              <Spacer width="8px" />
              <ArrowRight width={16} />
            </FlexRow>
          )}
        </Button>
        <Spacer width="32px" />
      </FlexRow>
      <Spacer height={24} />
    </PageWrapper>
  );
};

const Heading1Text = styled.h1`
  margin-top: 0;
  margin-bottom: 8px;
  color: ${({ theme }) => theme.globalConstants.color.brandSecondary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.heading};
  font-weight: 900;
  font-size: 28px;

  ${({ theme }) => theme.mediaQueries.largeMobile`
    font-size: 24px;
  `}
  ${({ theme }) => theme.mediaQueries.mobile`
    font-size: 20px;
  `}
`;

const InputWrapper = styled(TextInput)`
  width: 100%;
`;

const Container = styled.div<{
  isValid: boolean;
  showErrors: boolean;
}>`
  border-radius: 6px;
  ${({ isValid, showErrors, theme }) =>
    !isValid && showErrors
      ? `border: none; box-shadow: 0 0 0 2px ${theme.globalConstants.color.textDanger}`
      : ``}
`;

const WaiverPreview = styled.div`
  max-height: 480px;
  overflow-y: auto;
  padding: 24px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;
  background: ${({ theme }) => theme.globalConstants.color.white};
`;

const DisabledDateTextInput = styled(DateTextInput)`
  &:disabled {
    background-color: ${({ theme }) =>
      theme.globalConstants.color.backgroundGray};
    border-color: ${({ theme }) => theme.globalConstants.color.borderGray};
    color: ${({ theme }) => theme.globalConstants.color.textSecondary};
    -webkit-text-fill-color: ${({ theme }) =>
      theme.globalConstants.color.textSecondary};
    filter: none;
    opacity: 1;
  }
`;

const PersonalInformationLink = styled(RouterLink)`
  color: ${({ theme }) => theme.globalConstants.color.brandPrimary};
  font-weight: 700;
  text-decoration: underline;
`;

const ValidationMessage = styled.p`
  margin-top: 8px;
  color: ${({ theme }) => theme.globalConstants.color.textDanger};
`;

const FlexRow = styled.div`
  align-items: center;
  display: flex;
`;

const DownloadWaiver = styled(Link)`
  text-decoration: none !important;
  margin-top: 12px;
`;

export default ConfirmationPage;
